<div class="data-gen-panel">
  <h5>Hayagriva — Data Generation</h5>

  <!-- ── Section 1: Reset Structure ───────────────────────────────────────── -->
  <section class="mb-4">
    <h6>1. Reset Structure</h6>
    <p class="text-muted small">
      Wipes all existing tenant data and re-creates the app structure — Libraries and Folders
      as BizEntity records. No files are written. Use this to reset the data layer
      before running <em>Generate Files</em>, or when you only need a clean structure.
    </p>
    <div class="alert alert-warning py-2 small mb-3">
      ⚠️ This permanently deletes all existing data for the current tenant.
    </div>
    <div class="form-check mb-2">
      <input
        class="form-check-input"
        type="checkbox"
        id="confirm-seed"
        [ngModel]="confirmSeed()"
        (ngModelChange)="confirmSeed.set($event)"
        [disabled]="isSeedingOnly()"
      />
      <label class="form-check-label small" for="confirm-seed">
        I understand this will <strong>delete existing data</strong> and re-create a fresh structure
      </label>
    </div>
    <button
      class="btn btn-warning btn-sm"
      [disabled]="isSeedingOnly() || !confirmSeed()"
      (click)="seedOnly()"
    >
      @if (isSeedingOnly()) {
        <span class="spinner-border spinner-border-sm me-1" role="status"></span>
        Seeding…
      } @else {
        Reset Structure
      }
    </button>
    @if (seedResult(); as sr) {
      <div class="alert mt-2 mb-0 py-2 small"
           [class.alert-success]="sr.success"
           [class.alert-danger]="!sr.success">
        @if (sr.success) { ✅ Structure created successfully. }
        @else { ❌ {{ sr.error ?? 'Failed' }} }
      </div>
    }
  </section>

  <hr />

  <!-- ── Section 2: Generate Files ────────────────────────────────────────── -->
  <section class="mb-3">
    <h6>2. Generate Files</h6>
    <p class="text-muted small">
      Seeds Libraries and Folders first, then uploads real files for each folder
      via <code>FileOperationsService</code> — the same pipeline as the production
      "New file" button. Files are fully openable after generation.
    </p>

    <!-- Pack selector -->
    <div class="card card-body bg-light mb-3">
      <h6 class="mb-2">Data Pack</h6>
      <div class="d-flex flex-column gap-2">
        @for (pack of packs; track pack.id) {
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              [id]="'pack-' + pack.id"
              [value]="pack.id"
              [checked]="selectedPackId() === pack.id"
              (change)="selectPack(pack.id)"
            />
            <label class="form-check-label" [for]="'pack-' + pack.id">
              <strong>{{ pack.displayName }}</strong>
              <span class="text-muted ms-2 small">{{ pack.description }}</span>
            </label>
          </div>
        }
      </div>
    </div>

    <!-- File type counts (dense pack only) -->
    @if (showConfigForm()) {
      <div class="card card-body bg-light mb-3">
        <h6 class="mb-2">Files per folder</h6>
        <p class="text-muted small mb-3">
          Each count applies to every folder in the seeded Libraries.
        </p>
        <div class="row g-2">
          @for (entry of fileTypeEntries(); track entry[0]) {
            <div class="col-6 col-sm-4">
              <label class="form-label small">
                {{ fileTypeLabels[entry[0]] || entry[0] }}
              </label>
              <input
                class="form-control form-control-sm"
                type="number"
                min="0"
                max="20"
                [ngModel]="entry[1]"
                (ngModelChange)="updateFileTypeCount(entry[0], +$event)"
              />
            </div>
          }
        </div>
      </div>
    }

    <!-- Admin User Selection -->
    <div class="card card-body bg-light mb-3">
      <h6 class="mb-2">Admin Users</h6>
      <p class="text-muted small mb-2">
        Select which users should be added to the Admin Team.
        All checked users will receive admin access after data generation.
      </p>
      @if (isLoadingUsers()) {
        <div class="d-flex align-items-center gap-2 text-muted small">
          <span class="spinner-border spinner-border-sm" role="status"></span>
          Loading users…
        </div>
      } @else if (availableUsers().length === 0) {
        <p class="text-muted small mb-0">No users found.</p>
      } @else {
        <div class="d-flex gap-2 mb-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="selectAllAdminUsers()">Select all</button>
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="deselectAllAdminUsers()">Deselect all</button>
        </div>
        @for (user of availableUsers(); track user.name) {
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              [id]="'hayagriva-admin-' + user.name"
              [checked]="isAdminUserSelected(user.name)"
              (change)="toggleAdminUser(user.name)"
            />
            <label class="form-check-label" [for]="'hayagriva-admin-' + user.name">
              <strong>{{ user.name || 'Unnamed' }}</strong>
              @if (user.email) {
                <span class="text-muted ms-2 small">{{ user.email }}</span>
              }
            </label>
          </div>
        }
        <div class="text-muted small mt-1">
          {{ selectedAdminUserNames().size }} of {{ availableUsers().length }} users selected as admin
        </div>
      }
    </div>

    <button
      class="btn btn-primary btn-sm"
      [disabled]="isRunning()"
      (click)="generate()"
    >
      @if (isRunning()) {
        <span class="spinner-border spinner-border-sm me-1" role="status"></span>
        Generating…
      } @else {
        Generate — {{ selectedPack().displayName }} Pack
      }
    </button>

    @if (result(); as res) {
      <div class="alert mt-3 mb-0"
           [class.alert-success]="res.success"
           [class.alert-danger]="!res.success">
        @if (res.success) {
          Done — {{ res.filesCreated }} file(s) created.
        } @else {
          Failed ({{ res.filesCreated }} created, {{ res.filesFailed }} failed){{ res.error ? ': ' + res.error : '' }}
        }
      </div>
    }
  </section>

  <!-- ── Shared log ────────────────────────────────────────────────────────── -->
  @if (logs().length > 0) {
    <div class="mt-3">
      <p class="small fw-semibold mb-1">Log ({{ logs().length }} entries)</p>
      <pre
        class="bg-dark text-light p-3 rounded small overflow-auto"
        style="max-height: 340px"
      >{{ logs().join('\n') }}</pre>
    </div>
  }
</div>
